/**
 * Enum representing the quality seal status of an entity.
 *
 * @public
 */
export enum QualitySealStatus {
  /** Entity data is fresh and verified within the approved window */
  APPROVED = 'APPROVED',
  /** Entity data is approaching the staleness threshold and needs review */
  REVIEW_PENDING = 'REVIEW_PENDING',
  /** Entity data has exceeded the staleness threshold and is unverified */
  UNVERIFIED = 'UNVERIFIED',
  /** Entity has remained unverified for the governance inactivity threshold */
  INACTIVE = 'INACTIVE',
}

export enum QualitySealStatusLabel {
  APPROVED = 'Approved',
  REVIEW_PENDING = 'Review Pending',
  UNVERIFIED = 'Unverified',
  INACTIVE = 'Inactive',
}

/**
 * Information about the quality seal of an entity.
 *
 * @public
 */
export type QualitySealInfo = {
  /** The current quality seal status */
  status: QualitySealStatus;
  /** ISO timestamp of when the entity was last verified (manual override or last_updated_at) */
  lastVerifiedAt: string;
  /** ISO timestamp of when the entity was last updated in the catalog */
  lastUpdatedAt: string;
  /** Whether the current seal is based on a manual override */
  isManualOverride: boolean;
};

export type QualitySealAction = {
  iconKey: QualitySealStatus | 'RENEW';
  value: QualitySealStatus;
  label: string;
  darkColor: string;
  lightColor: string;
  /** Icon color in dark mode, when it differs from `darkColor`. */
  iconDarkColor?: string;
  /** Icon color in light mode, when it differs from `lightColor`. */
  iconLightColor?: string;
};

/**
 * quality seal properties for colored representation everywhere
 */
export type QualitySealProperties = {
  value: QualitySealStatus;
  label: QualitySealStatusLabel;
  darkColor: string;
  lightColor: string;
  /** Icon color in dark mode, when it differs from `darkColor`. */
  iconDarkColor?: string;
  /** Icon color in light mode, when it differs from `lightColor`. */
  iconLightColor?: string;
  description: string;
  tooltip: string;
  actions: QualitySealAction[];
};
